// Identity lifecycle: create, persist, load, recover (ARCHITECTURE.md §3, §13.1).
//
// The recovery phrase is shown ONCE at creation and never stored. What we keep
// in IndexedDB is the derived KeyBundle (private keys non-extractable) plus the
// public identifier and a "seed backed up" flag. Losing the phrase without
// having stored these keys = unrecoverable identity (the decentralization cost,
// surfaced clearly in the UI).

import { deriveIdentity, generateMnemonic, validateMnemonic, type KeyBundle } from './keys';
import { idbClearAll, idbGet, idbPut, STORE_IDENTITY } from './db';

const ME = 'me';

export interface StoredIdentity {
  circleId: string;
  keys: KeyBundle;
  createdAt: number;
  seedBackedUp: boolean;
}

/**
 * Clear every store this app writes to, locally: IndexedDB (identity, friends,
 * cache) and any `circle:`-prefixed localStorage key (profile edit drafts, UI
 * prefs). Shared by `wipeLocal` and by adopt/recover below — the latter call
 * it as a precondition, not just a UI-triggered action, so the "one local
 * identity at a time" invariant holds even if a future screen (or a bug in
 * the router) ever reaches adopt/recover without having wiped first: writing
 * a new identity must never leave a previous one's profile/friends/groups
 * behind for the new identity to unknowingly inherit and publish.
 */
async function clearAllLocalData(): Promise<void> {
  await idbClearAll();
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith('circle:')) localStorage.removeItem(key);
    }
  } catch {
    /* localStorage unavailable — nothing sensitive was written there either */
  }
}

let current: StoredIdentity | null = null;

/** The in-memory identity for this session, if loaded. */
export function currentIdentity(): StoredIdentity | null {
  return current;
}

/** Load a previously created identity from storage into the session. */
export async function loadIdentity(): Promise<StoredIdentity | null> {
  const stored = await idbGet<StoredIdentity>(STORE_IDENTITY, ME);
  current = stored ?? null;
  return current;
}

/** An identity derived in memory but not yet written to disk. */
export interface PreparedIdentity {
  mnemonic: string;
  circleId: string;
  keys: KeyBundle;
}

/**
 * Derive a brand-new identity **without persisting it**. Onboarding calls this
 * early (while the visitor reads the intro) so the key derivation is already
 * done when they commit, then `adoptIdentity` writes it. Nothing is stored
 * until then, so someone who walks away mid-onboarding gets the full intro
 * again rather than a half-created account.
 */
export async function prepareIdentity(strengthBits: 128 | 256 = 128): Promise<PreparedIdentity> {
  const mnemonic = generateMnemonic(strengthBits);
  const derived = await deriveIdentity(mnemonic);
  return { mnemonic, circleId: derived.circleId, keys: derived.keys };
}

/**
 * Persist a prepared identity, with seedBackedUp=false until confirmed.
 * Clears any local data first (see `clearAllLocalData`) — on the intended
 * fresh-device path this is a no-op, but it guarantees a new identity never
 * inherits a previous one's profile/friends/groups.
 */
export async function adoptIdentity(prepared: PreparedIdentity): Promise<StoredIdentity> {
  await clearAllLocalData();
  const identity: StoredIdentity = {
    circleId: prepared.circleId,
    keys: prepared.keys,
    createdAt: Date.now(),
    seedBackedUp: false,
  };
  await idbPut(STORE_IDENTITY, ME, identity);
  current = identity;
  return identity;
}

/**
 * Create a brand-new identity. Returns the recovery phrase to display ONCE.
 * The identity is persisted immediately with seedBackedUp=false.
 */
export async function createIdentity(strengthBits: 128 | 256 = 128): Promise<{ mnemonic: string; identity: StoredIdentity }> {
  const prepared = await prepareIdentity(strengthBits);
  const identity = await adoptIdentity(prepared);
  return { mnemonic: prepared.mnemonic, identity };
}

/**
 * Recover an identity from an existing recovery phrase. Clears any local data
 * first (see `clearAllLocalData`) so a device that unexpectedly still held a
 * different identity's profile/friends/groups doesn't hand them to the
 * recovered one — `restoreMyProfileFromRelay` is what's meant to repopulate
 * this device afterwards, from the recovered identity's own encrypted backup.
 */
export async function recoverIdentity(mnemonic: string): Promise<StoredIdentity> {
  const normalized = mnemonic.trim().replace(/\s+/g, ' ');
  if (!validateMnemonic(normalized)) {
    throw new Error('Phrase non reconnue. Vérifiez l’orthographe et l’ordre des mots.');
  }
  const derived = await deriveIdentity(normalized);
  await clearAllLocalData();
  const identity: StoredIdentity = {
    circleId: derived.circleId,
    keys: derived.keys,
    createdAt: Date.now(),
    seedBackedUp: true, // they demonstrably have the phrase
  };
  await idbPut(STORE_IDENTITY, ME, identity);
  current = identity;
  return identity;
}

/** Mark the recovery phrase as safely written down. */
export async function markSeedBackedUp(): Promise<void> {
  if (!current) throw new Error('Aucune identité chargée');
  current = { ...current, seedBackedUp: true };
  await idbPut(STORE_IDENTITY, ME, current);
}

/**
 * Wipe local data. Without the recovery phrase this is irreversible — the
 * caller must confirm the user understands before calling.
 */
export async function wipeLocal(): Promise<void> {
  await clearAllLocalData();
  current = null;
}
